import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useConfig } from '../contexts/ConfigContext.js';

/**
 * Handles auth + admin verification for admin pages.
 * Waits for both Firebase auth AND Firestore config to be ready before
 * making any redirect decision, so adminEmails can live solely in Firestore.
 *
 * Returns { currentUser, isReady }
 *   - currentUser: the Firebase user object once confirmed admin, null otherwise
 *   - isReady: true once the user is confirmed admin and config is loaded
 */
export function useAdminGuard() {
  const navigate = useNavigate();
  const location = useLocation();
  const config = useConfig();
  const [authUser, setAuthUser] = useState(undefined); // undefined = not yet known

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, user => setAuthUser(user ?? null));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (authUser === undefined || !config.configLoaded) return;

    if (!authUser) {
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    if (!config.adminEmails.includes(authUser.email)) {
      navigate('/');
    }
  }, [authUser, config.configLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const isReady =
    authUser !== undefined &&
    authUser !== null &&
    config.configLoaded &&
    config.adminEmails.includes(authUser.email);

  return { currentUser: authUser ?? null, isReady };
}
